import React from 'react';
import { useClientContext } from '@/contexts/ClientContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, RefreshCw, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

export const ClientSwitcher = () => {
  const {
    currentClientId,
    currentClientName,
    availableClients,
    loading,
    canSwitchClients,
    showAllClients,
    switchClient,
    invalidateClientQueries,
  } = useClientContext();

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <Skeleton className="h-9 w-48" />
      </div>
    );
  }

  // Se não pode alternar, mostrar apenas o cliente atual
  if (!canSwitchClients) {
    if (!currentClientName) return null;
    
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{currentClientName}</span>
      </div>
    );
  }

  // Determinar o valor atual do select
  const currentValue = showAllClients ? 'all' : (currentClientId || undefined);
  
  // Determinar o texto a exibir
  const displayName = showAllClients ? 'Todos os Clientes' : (currentClientName || 'Selecionar cliente');

  return (
    <div className="flex items-center gap-2">
      {showAllClients ? (
        <Globe className="h-4 w-4 text-primary" />
      ) : (
        <Building2 className="h-4 w-4 text-muted-foreground" />
      )}
      
      <Select
        value={currentValue}
        onValueChange={(value) => switchClient(value)}
      >
        <SelectTrigger className="w-[220px] h-9">
          <SelectValue placeholder="Selecionar cliente">
            <div className="flex items-center gap-2">
              {showAllClients && <Globe className="h-3 w-3" />}
              <span className="truncate">{displayName}</span>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {/* Opção "Todos os Clientes" - apenas para quem tem múltiplos clientes */}
          {availableClients.length > 1 && (
            <SelectItem value="all">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-primary" />
                <span>Todos os Clientes</span>
                {showAllClients && (
                  <Badge variant="secondary" className="text-xs ml-1">
                    Ativo
                  </Badge>
                )}
              </div>
            </SelectItem>
          )}
          
          {/* Lista de clientes individuais */}
          {availableClients.map((client) => (
            <SelectItem key={client.id} value={client.id}>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span>{client.name}</span>
                {client.id === currentClientId && !showAllClients && (
                  <Badge variant="secondary" className="text-xs ml-1">
                    Atual
                  </Badge>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        variant="ghost"
        size="icon"
        onClick={() => invalidateClientQueries()}
        title="Atualizar dados"
        className="h-9 w-9"
      >
        <RefreshCw className="h-4 w-4" />
      </Button>
    </div>
  );
};
